// AiSummary.jsx
import React from 'react';
import { Paper, Typography, CircularProgress } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { Description as DescriptionIcon } from '@material-ui/icons';
import { formatBuddyBooksLevelDisplay } from '../../../../helpers';

const styles = (theme) => ({
    root: {
        backgroundColor: '#fff',
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        minHeight: 120,
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
    },
    header: {
        backgroundColor: '#484CA0',
        padding: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    title: {
        fontWeight: 'bold',
        color: '#fff',
        fontSize: '1.1rem',
    },
    robotIcon: {
        color: '#fff',
        fontSize: '1.5rem',
    },
    content: {
        padding: theme.spacing(3),
    },
    summaryText: {
        color: '#555',
        lineHeight: 1.6,
        fontSize: '1rem',
    },
    placeholder: {
        color: '#666',
        fontStyle: 'italic',
        textAlign: 'center',
        padding: theme.spacing(4),
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4),
    },
    error: {
        color: '#d32f2f',
        textAlign: 'center',
        padding: theme.spacing(2),
    },
    keywordHighlight: {
        fontWeight: 'bold',
        color: '#000', // Black for maximum contrast
        backgroundColor: '#f5f5f5', // Very subtle gray background
        padding: '1px 2px',
        borderRadius: '2px',
    },
});

const AiSummary = ({ classes, summary, loading, error, currentBuddyBooksLevel }) => {
    // Helper function to format the date array
    const formatDate = (dateArray) => {
        if (!dateArray || !Array.isArray(dateArray) || dateArray.length < 3) {
            return '';
        }
        
        try {
            // dateArray format: [year, month, day, hour, minute, second, nanoseconds]
            const [year, month, day] = dateArray;
            const date = new Date(year, month - 1, day); // month is 0-indexed in JavaScript Date
            
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    // Helper function to format text with bold keywords
    const formatSummaryText = (text) => {
        // Check if text already has **word** formatting
        if (text.includes('**')) {
            // Split by ** to create bold spans
            const parts = text.split('**');
            return parts.map((part, index) => {
                if (index % 2 === 1) {
                    return <strong key={index} className={classes.keywordHighlight}>{part}</strong>;
                }
                return part;
            });
        }
        
        // Fallback for development: use dummy word list if not in production
        const isProduction = process.env.NODE_ENV === 'production';
        if (!isProduction) {
            const dummyBoldWordList = ['accuracy', 'fluency', 'reading', 'progress', 'improvement', 'scores'];
            
            let formattedText = text;
            dummyBoldWordList.forEach(keyword => {
                const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
                formattedText = formattedText.replace(regex, `**${keyword}**`);
            });
            
            // Split by ** to create bold spans
            const parts = formattedText.split('**');
            return parts.map((part, index) => {
                if (index % 2 === 1) {
                    return <strong key={index} className={classes.keywordHighlight}>{part}</strong>;
                }
                return part;
            });
        }
        
        // Production: return text as-is if no ** formatting found
        return text;
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className={classes.loading}>
                    <CircularProgress size={24} />
                    <Typography style={{ marginLeft: 16 }}>Loading summary...</Typography>
                </div>
            );
        }

        if (error) {
            return (
                <Typography className={classes.error}>
                    {error}
                </Typography>
            );
        }

        if (summary && summary.summary) {
            // Split the summary by double newlines to create paragraphs
            const paragraphs = summary.summary.split('\n\n').filter(p => p.trim());
            
            const renderParagraphOrList = (paragraph, index) => {
                const trimmedParagraph = paragraph.trim();
                
                // Check if this paragraph contains bullet points
                if (trimmedParagraph.includes('\n-')) {
                    // Split into title and bullet points
                    const parts = trimmedParagraph.split('\n-');
                    const title = parts[0].trim();
                    const bulletPoints = parts.slice(1).map(point => point.trim());
                    
                    return (
                        <div key={index} style={{ marginBottom: index < paragraphs.length - 1 ? '16px' : '0' }}>
                            {title && (
                                <Typography className={classes.summaryText} style={{ marginBottom: '8px' }}>
                                    {formatSummaryText(title)}
                                </Typography>
                            )}
                            <ul style={{ margin: '0 0 0 20px', padding: 0, listStyleType: 'disc' }}>
                                {bulletPoints.map((point, pointIndex) => (
                                    <li key={pointIndex} style={{ marginBottom: '4px' }}>
                                        <Typography className={classes.summaryText} component="span">
                                            {formatSummaryText(point)}
                                        </Typography>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                }
                
                // Regular paragraph
                return (
                    <Typography 
                        key={index} 
                        className={classes.summaryText}
                        style={{ 
                            marginBottom: index < paragraphs.length - 1 ? '16px' : '0'
                        }}
                    >
                        {formatSummaryText(trimmedParagraph)}
                    </Typography>
                );
            };
            
            return (
                <div>
                    {paragraphs.map((paragraph, index) => renderParagraphOrList(paragraph, index))}
                </div>
            );
        }

        return (
            <Typography className={classes.placeholder}>
                No summary available at this time.
            </Typography>
        );
    };

    return (
        <Paper className={classes.root}>
            <div className={classes.header}>
                <DescriptionIcon className={classes.robotIcon} />
                <Typography variant="h5" className={classes.title}>
                    Learning Assistant Summary
                    {summary && summary.date && (
                        <span style={{ fontWeight: 'normal', fontSize: '0.9em' }}>
                            {' '}({formatDate(summary.date)})
                        </span>
                    )}
                    <span style={{ fontWeight: 'normal', fontSize: '0.9em', marginLeft: 8 }}>
                        {currentBuddyBooksLevel ? ` (Buddy Books Level: ${formatBuddyBooksLevelDisplay(currentBuddyBooksLevel)})` : ' (Student has not yet been assigned a Leveled Book List)'}
                    </span>
                </Typography>
            </div>
            <div className={classes.content}>
                {renderContent()}
            </div>
        </Paper>
    );
};

export default withStyles(styles)(AiSummary);